<template>
  <div class="leo-operator-assistant">
    <div
      v-show="panelOpen"
      ref="panel"
      class="leo-assistant-float-panel"
      :class="{ 'leo-assistant-float-panel--chat': mode === 'chat' }"
      :style="panelStyle"
    >
      <header class="leo-assistant-float-head" @mousedown.prevent="onPanelDragStart">
        <span class="leo-assistant-float-title">Leo SOC助手</span>
        <div class="leo-assistant-float-actions">
          <button
            type="button"
            class="leo-assistant-float-iconbtn leo-assistant-float-iconbtn--close"
            title="关闭"
            aria-label="close"
            @click.stop="close"
          >×</button>
        </div>
      </header>

      <div class="assistant-shell" :class="{ 'assistant-shell--chat': mode === 'chat' }">
        <!-- 本页说明（对话 Tab 收成一行，避免挤占聊天） -->
        <section
          v-if="(pageFunctionZh || (pageStepsZh && pageStepsZh.length)) && mode !== 'chat'"
          class="assistant-card"
          data-testid="soc-assistant-page-help"
        >
          <header class="assistant-card__head">
            <div class="assistant-card__title-row">
              <h3 class="assistant-card__title">{{ pageHelpTitleZh || '本页说明' }}</h3>
              <el-tag size="mini" type="info" effect="plain">页面指引</el-tag>
            </div>
          </header>
          <div class="assistant-card__body">
            <p v-if="pageFunctionZh" class="assistant-page-help__fn">{{ pageFunctionZh }}</p>
            <ol v-if="pageStepsZh && pageStepsZh.length" class="assistant-page-help__steps">
              <li v-for="(s, i) in pageStepsZh" :key="i">{{ s }}</li>
            </ol>
          </div>
        </section>
        <p
          v-else-if="mode === 'chat' && (pageHelpTitleZh || pageFunctionZh)"
          class="assistant-page-preface assistant-page-preface--compact"
        >
          {{ pageHelpTitleZh || '本页' }}{{ pageFunctionZh ? ' · ' + pageFunctionZh : '' }}
        </p>
        <p v-else-if="pagePreface && mode !== 'chat'" class="assistant-page-preface">{{ pagePreface }}</p>

        <!-- 三模式 -->
        <div class="assistant-mode-bar" role="tablist">
          <el-radio-group v-model="mode" size="small">
            <el-radio-button label="manual">{{ $t('route.socAssistantTabManual') }}</el-radio-button>
            <el-radio-button label="guide">{{ $t('route.socAssistantTabGuide') }}</el-radio-button>
            <el-radio-button label="chat">{{ $t('route.socAssistantTabChat') }}</el-radio-button>
          </el-radio-group>
        </div>

        <!-- Leo 对话 -->
        <section v-if="mode === 'chat'" class="assistant-card assistant-card--chat">
          <div class="assistant-card__body assistant-card__body--chat">
            <iframe
              class="assistant-chat-iframe"
              :src="chatFrameSrc"
              :key="chatFrameKey"
              title="Leo chat"
              data-testid="soc-assistant-leo-chat"
            />
          </div>
        </section>

        <!-- 智能引导 -->
        <section v-else-if="mode === 'guide'" class="assistant-card assistant-card--nest">
          <soc-smart-guide-panel />
        </section>

        <!-- 问说明书 -->
        <div v-else class="assistant-manual">
          <section class="assistant-card">
            <header class="assistant-card__head">
              <div class="assistant-card__title-row">
                <h3 class="assistant-card__title">快捷问法</h3>
                <el-tag
                  v-if="topicScope"
                  size="mini"
                  :type="topicScopeTagType"
                  class="assistant-scope-tag"
                >{{ topicScopeLabel }}</el-tag>
              </div>
            </header>
            <div class="assistant-card__body">
              <p class="muted assistant-hint">
                已感知当前页面；快捷问法仅显示本页相关。也可直接下方提问。
              </p>
              <div v-if="quickTopics.length" class="assistant-topics">
                <el-button
                  v-for="t in quickTopics"
                  :key="t.id"
                  size="mini"
                  plain
                  :loading="loading && question === t.question"
                  @click="askQuick(t.question)"
                >{{ t.labelZh }}</el-button>
              </div>
              <p v-else-if="topicScope === 'empty'" class="assistant-empty-topics muted">
                本页暂无快捷问法，请直接在下方输入本页相关问题。
              </p>
            </div>
          </section>

          <section class="assistant-card">
            <header class="assistant-card__head">
              <div class="assistant-card__title-row">
                <h3 class="assistant-card__title">提问</h3>
              </div>
            </header>
            <div class="assistant-card__body">
              <div v-if="clarifyOptions.length" class="assistant-clarify">
                <p class="assistant-clarify-prompt">{{ clarifyPrompt }}</p>
                <div class="assistant-clarify-opts">
                  <el-button
                    v-for="(opt, idx) in clarifyOptions"
                    :key="idx"
                    size="mini"
                    type="primary"
                    plain
                    @click="askClarifyOption(opt)"
                  >{{ opt.label }}</el-button>
                </div>
              </div>
              <el-input
                v-model="question"
                type="textarea"
                :rows="3"
                resize="none"
                placeholder="输入问题，例如：这页设备列表怎么搜 IMIE？"
              />
              <div class="assistant-actions">
                <el-button
                  type="primary"
                  size="small"
                  :loading="loading"
                  @click="ask"
                >提问</el-button>
                <el-button
                  type="danger"
                  size="small"
                  plain
                  :disabled="!answer || loading"
                  @click="onWrongAnswer"
                >回答不对</el-button>
              </div>
            </div>
          </section>

          <section v-if="answer" class="assistant-card">
            <header class="assistant-card__head">
              <div class="assistant-card__title-row">
                <h3 class="assistant-card__title">回答</h3>
                <el-tag
                  v-if="answerLayerZh"
                  size="mini"
                  :type="answerLayerTagType"
                  class="assistant-layer-tag"
                >{{ answerLayerZh }}</el-tag>
              </div>
            </header>
            <div class="assistant-card__body">
              <pre class="assistant-answer-body">{{ answer }}</pre>
              <p v-if="disclaimer" class="muted assistant-disclaimer">{{ disclaimer }}</p>
            </div>
          </section>
        </div>
      </div>

      <div
        class="leo-assistant-float-resize"
        title="拖曳右下角调整大小"
        aria-hidden="true"
        @mousedown.stop.prevent="onPanelResizeStart"
      />
    </div>
  </div>
</template>

<script>
import {
  fetchOperatorSupportTopics,
  askOperatorSupport,
  recordOperatorPageView,
  feedbackOperatorSupport,
  buildOperatorPageContext
} from '@/api/leoTrainingOperatorSupport'
import {
  leoOperatorAssistantBus,
  setLeoOperatorAssistantOpen,
  resolveHashModeRouteUrl
} from '@/utils/leoOperatorAssistant'
import { setLeoFloatDockOpen, getLeoFloatDockOpen } from '@/utils/leoAdminFloatDock'
import {
  getAssistantPanelLayout,
  setAssistantPanelLayout
} from '@/utils/leoOperatorAssistantLayout'
import SocSmartGuidePanel from '@/components/LeoKnowledge/SocSmartGuidePanel.vue'

const DEFAULT_W = 480
const DEFAULT_H = 640
const DEFAULT_CHAT_W = 560
const DEFAULT_CHAT_H = 720
const MIN_W = 320
const MIN_H = 360
const VIEW_PAD = 8

export default {
  name: 'LeoTrainingOperatorAssistant',
  components: { SocSmartGuidePanel },
  data () {
    return {
      panelOpen: false,
      mode: 'manual',
      panelW: DEFAULT_W,
      panelH: DEFAULT_H,
      panelPos: null,
      dragging: false,
      dragDx: 0,
      dragDy: 0,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartW: 0,
      resizeStartH: 0,
      resizeAnchor: null,
      loading: false,
      question: '',
      answer: '',
      disclaimer: '',
      pagePreface: '',
      pageHelpTitleZh: '',
      pageFunctionZh: '',
      pageStepsZh: [],
      quickTopics: [],
      topicScope: '',
      answerLayer: '',
      answerLayerZh: '',
      clarifyPrompt: '',
      clarifyOptions: [],
      clarifySlot: ''
    }
  },
  computed: {
    panelStyle () {
      const size = {
        width: this.panelW + 'px',
        height: this.panelH + 'px'
      }
      if (this.panelPos && typeof this.panelPos.left === 'number' && typeof this.panelPos.top === 'number') {
        return Object.assign({}, size, {
          left: this.panelPos.left + 'px',
          top: this.panelPos.top + 'px',
          right: 'auto',
          bottom: 'auto'
        })
      }
      return Object.assign({}, size, {
        right: '24px',
        bottom: '100px',
        left: 'auto',
        top: 'auto'
      })
    },
    chatFrameKey () {
      const ctx = this.pageContext() || {}
      return [ctx.routePath || '', ctx.menuId || '', ctx.pageTitle || ''].join('|')
    },
    chatFrameSrc () {
      const ctx = this.pageContext() || {}
      const q = {
        surface: 'soc_admin',
        routePath: ctx.routePath || this.$route.path || '',
        routeName: ctx.routeName || this.$route.name || '',
        menuId: ctx.menuId != null ? String(ctx.menuId) : '',
        pageTitle: ctx.pageTitle || ''
      }
      return resolveHashModeRouteUrl(this.$router, {
        name: 'leoStandaloneChat',
        query: q
      })
    },
    topicScopeLabel () {
      if (this.topicScope === 'page') return '本页快捷'
      if (this.topicScope === 'empty') return '本页暂无快捷'
      if (this.topicScope === 'global') return '全局快捷'
      return ''
    },
    topicScopeTagType () {
      if (this.topicScope === 'page') return 'success'
      if (this.topicScope === 'empty') return 'info'
      return ''
    },
    answerLayerTagType () {
      if (this.answerLayer === 'page') return 'success'
      if (this.answerLayer === 'related') return 'warning'
      if (this.answerLayer === 'empty') return 'info'
      return ''
    }
  },
  watch: {
    '$route.fullPath' () {
      this.onRouteChange()
    },
    panelOpen (open) {
      setLeoOperatorAssistantOpen(!!open)
      if (open && this.mode === 'manual') {
        this.refreshTopics()
      }
      // 关浮窗时关掉顶栏「Leo 对话」开关，避免开关亮着却没窗
      if (!open && getLeoFloatDockOpen()) {
        setLeoFloatDockOpen(false)
      }
    },
    mode (m) {
      if (m === 'chat') {
        this.$nextTick(() => this.ensurePanelSizeForMode())
      } else if (m === 'manual' && this.panelOpen) {
        this.refreshTopics()
      }
    }
  },
  mounted () {
    this.restorePanelLayout()
    setLeoOperatorAssistantOpen(this.panelOpen)
    leoOperatorAssistantBus.$on('open', this.open)
    leoOperatorAssistantBus.$on('close', this.close)
    leoOperatorAssistantBus.$on('open-smart-guide', this.openSmartGuide)
    this.reportPageContext()
  },
  beforeDestroy () {
    setLeoOperatorAssistantOpen(false)
    leoOperatorAssistantBus.$off('open', this.open)
    leoOperatorAssistantBus.$off('close', this.close)
    leoOperatorAssistantBus.$off('open-smart-guide', this.openSmartGuide)
    window.removeEventListener('mousemove', this.onPanelDragMove)
    window.removeEventListener('mouseup', this.onPanelDragEnd)
    window.removeEventListener('mousemove', this.onPanelResizeMove)
    window.removeEventListener('mouseup', this.onPanelResizeEnd)
  },
  methods: {
    restorePanelLayout () {
      const lay = getAssistantPanelLayout()
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1920
      const vh = typeof window !== 'undefined' ? window.innerHeight : 1080
      if (lay) {
        this.panelW = Math.min(Math.max(MIN_W, Math.round(lay.width)), vw - 2 * VIEW_PAD)
        this.panelH = Math.min(Math.max(MIN_H, Math.round(lay.height)), vh - 2 * VIEW_PAD)
        this.panelPos = {
          left: Math.min(Math.max(VIEW_PAD, Math.round(lay.left)), vw - this.panelW - VIEW_PAD),
          top: Math.min(Math.max(VIEW_PAD, Math.round(lay.top)), vh - this.panelH - VIEW_PAD)
        }
      } else {
        this.panelW = DEFAULT_W
        this.panelH = DEFAULT_H
        this.panelPos = null
      }
    },
    persistPanelLayout () {
      const payload = {
        width: this.panelW,
        height: this.panelH
      }
      if (this.panelPos) {
        payload.left = this.panelPos.left
        payload.top = this.panelPos.top
      }
      setAssistantPanelLayout(payload)
    },
    ensurePanelSizeForMode () {
      if (this.mode !== 'chat') return
      if (this.panelW < DEFAULT_CHAT_W) this.panelW = DEFAULT_CHAT_W
      if (this.panelH < Math.min(DEFAULT_CHAT_H, 560)) this.panelH = Math.min(DEFAULT_CHAT_H, 640)
      this.persistPanelLayout()
    },
    onPanelDragStart (e) {
      if (e.button !== 0) return
      const panel = this.$refs.panel
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      this.dragging = true
      this.dragDx = e.clientX - rect.left
      this.dragDy = e.clientY - rect.top
      if (!this.panelPos) {
        this.panelPos = { left: rect.left, top: rect.top }
      }
      window.addEventListener('mousemove', this.onPanelDragMove)
      window.addEventListener('mouseup', this.onPanelDragEnd)
    },
    onPanelDragMove (e) {
      if (!this.dragging) return
      let left = e.clientX - this.dragDx
      let top = e.clientY - this.dragDy
      const vw = window.innerWidth
      const vh = window.innerHeight
      left = Math.min(Math.max(VIEW_PAD, left), vw - this.panelW - VIEW_PAD)
      top = Math.min(Math.max(VIEW_PAD, top), vh - this.panelH - VIEW_PAD)
      this.panelPos = { left, top }
    },
    onPanelDragEnd () {
      if (!this.dragging) return
      this.dragging = false
      window.removeEventListener('mousemove', this.onPanelDragMove)
      window.removeEventListener('mouseup', this.onPanelDragEnd)
      this.persistPanelLayout()
    },
    onPanelResizeStart (e) {
      if (e.button !== 0) return
      const panel = this.$refs.panel
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      this.resizing = true
      this.resizeStartX = e.clientX
      this.resizeStartY = e.clientY
      this.resizeStartW = rect.width
      this.resizeStartH = rect.height
      this.resizeAnchor = { left: rect.left, top: rect.top }
      if (!this.panelPos) {
        this.panelPos = { left: rect.left, top: rect.top }
      }
      window.addEventListener('mousemove', this.onPanelResizeMove)
      window.addEventListener('mouseup', this.onPanelResizeEnd)
    },
    onPanelResizeMove (e) {
      if (!this.resizing || !this.resizeAnchor) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      const maxW = vw - this.resizeAnchor.left - VIEW_PAD
      const maxH = vh - this.resizeAnchor.top - VIEW_PAD
      let w = this.resizeStartW + (e.clientX - this.resizeStartX)
      let h = this.resizeStartH + (e.clientY - this.resizeStartY)
      w = Math.min(Math.max(MIN_W, w), Math.max(MIN_W, maxW))
      h = Math.min(Math.max(MIN_H, h), Math.max(MIN_H, maxH))
      this.panelW = Math.round(w)
      this.panelH = Math.round(h)
    },
    onPanelResizeEnd () {
      if (!this.resizing) return
      this.resizing = false
      this.resizeAnchor = null
      window.removeEventListener('mousemove', this.onPanelResizeMove)
      window.removeEventListener('mouseup', this.onPanelResizeEnd)
      this.persistPanelLayout()
    },
    pageContext () {
      return buildOperatorPageContext(this.$route, 'soc_admin', this.$store, this)
    },
    open (payload) {
      const mode = payload && payload.mode
      if (mode === 'manual' || mode === 'guide' || mode === 'chat') {
        this.mode = mode
      }
      this.panelOpen = true
      this.$nextTick(() => {
        this.ensurePanelSizeForMode()
        // 说明书／对话都要刷新本页说明；仅 report 不拉 topics 会导致聊天横幅空、答偏。
        if (this.mode === 'manual' || this.mode === 'chat') {
          this.refreshTopics()
        } else {
          this.reportPageContext()
        }
      })
    },
    openSmartGuide () {
      this.mode = 'guide'
      this.open({})
    },
    close () {
      this.panelOpen = false
    },
    onRouteChange () {
      this.reportPageContext()
      this.clearClarify()
      this.answer = ''
      this.disclaimer = ''
      this.answerLayer = ''
      this.answerLayerZh = ''
      if (this.panelOpen) {
        this.refreshTopics()
      } else {
        this.pagePreface = ''
        this.pageHelpTitleZh = ''
        this.pageFunctionZh = ''
        this.pageStepsZh = []
        this.topicScope = ''
      }
    },
    applyPageHelpFromTopics (data) {
      this.pagePreface = (data && data.pagePrefaceZh) || ''
      this.pageHelpTitleZh = (data && data.pageHelpTitleZh) || ''
      this.pageFunctionZh = (data && data.pageFunctionZh) || ''
      const steps = (data && data.pageStepsZh) || []
      this.pageStepsZh = Array.isArray(steps) ? steps : []
    },
    async reportPageContext () {
      try {
        await recordOperatorPageView(this.pageContext())
      } catch (e) {
        // non-blocking
      }
    },
    async refreshTopics () {
      try {
        const res = await fetchOperatorSupportTopics(this.pageContext())
        const data = res && res.data
        this.quickTopics = (data && data.quickTopics) || []
        this.topicScope = (data && data.topicScope) || ''
        this.applyPageHelpFromTopics(data)
      } catch (e) {
        this.quickTopics = []
        this.pagePreface = ''
        this.pageHelpTitleZh = ''
        this.pageFunctionZh = ''
        this.pageStepsZh = []
        this.topicScope = 'empty'
      }
    },
    askQuick (q) {
      this.question = q
      this.ask()
    },
    askClarifyOption (opt) {
      const label = (opt && opt.label) || ''
      if (!label) return
      this.question = label
      this.ask()
    },
    clearClarify () {
      this.clarifyPrompt = ''
      this.clarifyOptions = []
      this.clarifySlot = ''
    },
    async ask () {
      const q = (this.question || '').trim()
      if (!q) {
        this.$message.warning('请输入问题')
        return
      }
      this.loading = true
      this.clearClarify()
      this.answerLayer = ''
      this.answerLayerZh = ''
      try {
        const res = await askOperatorSupport({
          question: q,
          pageContext: this.pageContext()
        })
        const data = res && res.data
        if (data && data.phase === 'clarify') {
          this.answer = ''
          this.clarifyPrompt = data.clarifyPrompt || data.answerZh || ''
          this.clarifySlot = data.clarifySlot || ''
          this.clarifyOptions = data.clarifyOptions || []
          this.disclaimer = data.disclaimerZh || ''
          this.applyPageHelpFromTopics(data)
          return
        }
        this.answer = (data && data.answerZh) || '暂无回答'
        this.disclaimer = (data && data.disclaimerZh) || ''
        this.answerLayer = (data && data.answerLayer) || ''
        this.answerLayerZh = (data && data.answerLayerZh) || ''
        this.applyPageHelpFromTopics(data)
      } catch (e) {
        this.$message.error(e.message || '提问失败')
      } finally {
        this.loading = false
      }
    },
    onWrongAnswer () {
      if (!this.answer) return
      const apologyHtml = [
        '<p style="margin:0 0 12px;line-height:1.6;">非常抱歉，Leo 智能系统仍在持续学习中；若本次回答未能帮到您，我们深表歉意。</p>',
        '<p style="margin:0 0 12px;line-height:1.6;">您的反馈已同步至 Leo 后台训练系统，将用于改进后续回答。</p>',
        '<p style="margin:0;line-height:1.6;">感谢您的耐心与支持，我们会努力做得更好。</p>'
      ].join('')
      this.$alert(apologyHtml, '感谢您的反馈', {
        confirmButtonText: '知道了',
        dangerouslyUseHTMLString: true,
        customClass: 'leo-operator-wrong-answer-dialog'
      }).then(() => this.sendFeedback(false)).catch(() => {})
    },
    async sendFeedback (helpful) {
      if (!this.answer) return
      try {
        await feedbackOperatorSupport({
          question: this.question,
          answerZh: this.answer,
          helpful: helpful,
          reason: helpful ? '' : 'user_marked_wrong',
          pageContext: this.pageContext()
        })
        if (helpful) {
          this.$message.success('感谢反馈')
        }
      } catch (e) {
        this.$message.error('反馈失败')
      }
    }
  }
}
</script>

<style scoped>
.leo-assistant-float-panel {
  position: fixed;
  z-index: 100050;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 14px 44px rgba(15, 23, 42, 0.28);
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: var(--sap-color-page-bg, #f6f9fc);
  min-width: 320px;
  min-height: 360px;
}

.leo-assistant-float-panel--chat {
  min-height: 480px;
}

.leo-assistant-float-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  cursor: grab;
  user-select: none;
}

.leo-assistant-float-head:active {
  cursor: grabbing;
}

.leo-assistant-float-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.leo-assistant-float-actions {
  display: flex;
  gap: 4px;
}

.leo-assistant-float-iconbtn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.leo-assistant-float-iconbtn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.leo-assistant-float-iconbtn--close:hover {
  background: #dc2626;
}

.leo-assistant-float-resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 50%, rgba(64, 158, 255, 0.85) 50%);
  border-bottom-right-radius: 12px;
  z-index: 3;
}

.assistant-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sap-space-3, 12px);
  padding: 12px;
  overflow: auto;
}

.assistant-shell--chat {
  overflow: hidden;
  gap: var(--sap-space-2, 8px);
  padding: 8px 10px 12px;
}

.assistant-shell--chat .assistant-mode-bar {
  flex-shrink: 0;
}

.assistant-shell--chat .assistant-page-preface--compact {
  flex-shrink: 0;
  margin: 0;
}

.assistant-card--chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin: 0;
  overflow: hidden;
}

.assistant-card__body--chat {
  flex: 1;
  min-height: 0;
  height: 100%;
  padding: 0;
  display: flex;
  overflow: hidden;
}

.assistant-chat-iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 280px;
  border: 0;
  background: #fff;
}

.assistant-page-preface--compact {
  font-size: 12px;
  color: var(--sap-color-text-secondary, #606266);
  background: var(--sap-color-fill, #f8fafc);
  border-color: var(--sap-color-border, #e4e7ed);
}

.assistant-manual {
  display: flex;
  flex-direction: column;
  gap: var(--sap-space-4, 16px);
}

.assistant-card {
  background: var(--sap-color-surface, #fff);
  border: 1px solid var(--sap-color-border, #e4e7ed);
  border-radius: var(--sap-radius-lg, 8px);
  box-shadow: var(--sap-shadow-card, 0 1px 2px rgba(16, 24, 40, 0.04));
  overflow: hidden;
}

.assistant-card--nest {
  padding: 0;
}

.assistant-card--nest >>> .soc-smart-guide-panel {
  margin: 0;
  padding: var(--sap-space-4, 16px);
  background: transparent;
  border: none;
  border-radius: 0;
}

.assistant-card__head {
  padding: 10px var(--sap-space-4, 16px);
  border-bottom: 1px solid var(--sap-color-border, #e4e7ed);
  background: var(--sap-color-fill, #f8fafc);
}

.assistant-card__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 22px;
}

.assistant-card__title {
  margin: 0;
  font-size: var(--sap-font-size-md, 14px);
  font-weight: 600;
  line-height: 1.3;
  color: var(--sap-color-text, #303133);
}

.assistant-card__body {
  padding: var(--sap-space-4, 16px);
}

.assistant-mode-bar {
  display: flex;
  justify-content: stretch;
}

.assistant-mode-bar >>> .el-radio-group {
  display: flex;
  width: 100%;
}

.assistant-mode-bar >>> .el-radio-button {
  flex: 1;
}

.assistant-mode-bar >>> .el-radio-button__inner {
  width: 100%;
}

.assistant-page-help__fn {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--sap-color-text, #303133);
}

.assistant-page-help__steps {
  margin: 0;
  padding-left: 1.25em;
  font-size: 12px;
  line-height: 1.6;
  color: var(--sap-color-text-secondary, #606266);
}

.assistant-page-help__steps li + li {
  margin-top: 4px;
}

.assistant-page-preface {
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #409eff;
  background: #f0f7ff;
  border: 1px solid #d9ecff;
  border-radius: var(--sap-radius-lg, 8px);
}

.assistant-hint {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.5;
}

.assistant-scope-tag {
  flex-shrink: 0;
}

.assistant-empty-topics {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}

.assistant-clarify {
  margin: 0 0 12px;
  padding: 10px 12px;
  background: #fef9e7;
  border: 1px solid #f5e6b8;
  border-radius: 6px;
}

.assistant-clarify-prompt {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.5;
}

.assistant-clarify-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.assistant-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.assistant-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.assistant-answer-body {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.55;
  color: var(--sap-color-text, #303133);
}

.assistant-disclaimer {
  margin: 10px 0 0;
}

.assistant-layer-tag {
  font-weight: normal;
}

.muted {
  color: var(--sap-color-text-secondary, #909399);
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/LeoTrainingOperatorAssistant.vue